// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import ProfileBoardGroup from 'app/common/react/components/profile/ProfileBoards/ProfileBoardGroup';
import ProfileEmptyState from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyState';
import ProfileEmptyStateText from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyStateText';
import stopProfileTiming from 'app/common/react/components/profile/ProfileBoards/utils/stopProfileTiming';
import TestableScrollFetch from 'app/common/react/components/profile/ProfileBoards/TestableScrollFetch';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import type { DesktopProfileBoard } from 'app/packages/pinterest-flow-types/board';
import type { Dispatch } from 'redux';
import type { FeedItemKeyProfileBoard } from 'app/common/redux/reducers/feeds';
import type { ProfileBoardCardLayout } from 'app/common/react/components/profile/ProfileBoardCard/layout';
import type { Resource } from 'app/common/lib/react/withResource';
import type { Router } from 'app/common/lib/ReactRouter/types';
import withResource from 'app/common/lib/react/withResource';
import { compose } from 'redux';
import { createSelector } from 'reselect';
import { getBoardsDeleting } from 'app/common/redux/selectors/board';
import { invalidateOwnProfileBoards } from 'app/common/redux/actions/boards';
import { profileBoardsResource } from 'app/common/react/components/profile/ProfileBoards/utils/resources';
import { resourceInvalidate } from 'app/common/redux/actions/resourceFetch';
import { withRouter } from 'react-router';
import type { BoardSortOptionsType, ProfileUser } from 'app/packages/pinterest-flow-types/user';
import { Box, Spinner } from 'gestalt';
import { getProfileBoardFeed, getProfileBoardView } from 'app/common/redux/selectors/profileBoards';

type ReduxStateProps = {|
  boardKeys: Array<FeedItemKeyProfileBoard>,
  profileBoardView: 'list' | ProfileBoardCardLayout,
|};

type ReduxActionProps = {|
  invalidateFeeds: () => void,
  invalidatePinBetterSaveResource: () => void,
|};

type OwnProps = {|
  boardOrder?: BoardSortOptionsType,
  isOverview?: boolean,
  isOwnProfile: boolean,
  isUserBlocked?: boolean,
  mixPublicSecret?: boolean,
  user: ProfileUser,
  username: string,
|};

export type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxActionProps,
  boardsResource: Resource<Array<DesktopProfileBoard>>,
  router?: Router,
|};

export class ProfileBoards extends React.PureComponent<Props> {
  static defaultProps = {
    boardKeys: [],
  };

  componentDidMount() {
    const { isOwnProfile, boardsResource, user } = this.props;
    const { show_creator_profile: showCreatorProfile } = user;
    if (boardsResource.isLoaded) {
      stopProfileTiming({ isOwnProfile, showCreatorProfile, gridImagesCached: true });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { boardsResource } = this.props;
    if (
      boardsResource.isLoaded &&
      (prevProps.boardsResource.data || []).length > (boardsResource.data || []).length
    ) {
      window.scroll(0, 0); // boards have just been refreshed. scroll position is stale. scroll back to the top.
    }
  }

  componentWillUnmount() {
    const { invalidateFeeds } = this.props;
    if (this.feedIsStale) {
      invalidateFeeds();
    }
  }

  container: ?HTMLElement = null;
  feedIsStale: boolean = false;

  // TODO(xixia): general problem with the refreshData() functions are that it then resets the bookmarks and sets the
  // flags for loading in data back to false.
  handleBoardCreate = (board: DesktopProfileBoard) => {
    this.feedIsStale = true;
    const { router, invalidatePinBetterSaveResource } = this.props;
    invalidatePinBetterSaveResource();
    if (router) {
      router.push({
        pathname: board.url,
        state: {
          newBoard: true,
        },
      });
    }
  };

  handleBoardChanged = () => {
    this.feedIsStale = true;
  };

  handleBoardGroupChange = () => this.props.boardsResource.refreshData();

  groupBoards(boardKeys: Array<FeedItemKeyProfileBoard>) {
    const { mixPublicSecret } = this.props;

    return boardKeys.reduce(
      (boardsGroups, { id, profileGroup }) => {
        let grouping;
        if (profileGroup === 'public' || (mixPublicSecret && profileGroup === 'secret')) {
          // $FlowFixMe: new errors found from flow 0.96 upgrade
          grouping = { unlabeled: boardsGroups.unlabeled.concat([id]) };
        } else {
          // $FlowFixMe: new errors found from flow 0.96 upgrade
          grouping = { [profileGroup]: boardsGroups[profileGroup].concat([id]) };
        }

        return {
          ...boardsGroups,
          ...grouping,
        };
      },
      { unlabeled: [], protected: [], secret: [], archived: [] },
    );
  }

  renderAllBoardsGroups(boardKeys: Array<FeedItemKeyProfileBoard>) {
    const {
      isOwnProfile,
      isOverview,
      profileBoardView,
      boardOrder,
      isUserBlocked,
      user,
    } = this.props;
    const { is_partner: isPartner, show_creator_profile: showCreatorProfile } = user;

    if ((!isOwnProfile || isOverview) && !boardKeys.length) {
      return (
        <ProfileEmptyState>
          <ProfileEmptyStateText subject="boards" surface="profile" user={user} />
        </ProfileEmptyState>
      );
    }

    const boardsGroups = this.groupBoards(boardKeys);
    const groupsWithLength = ['protected', 'secret', 'archived'].filter(
      (group) => boardsGroups[group].length > 0,
    );

    return (
      <Box marginTop={profileBoardView === 'list' ? 6 : 0} marginBottom={6} overflow="hidden">
        {['unlabeled'].concat(isOwnProfile && !isOverview ? groupsWithLength : []).map((group) => (
          <ProfileBoardGroup
            key={group}
            boardIds={boardsGroups[group]}
            boardOrder={boardOrder}
            group={group}
            isOwnProfile={isOwnProfile}
            isOverview={!!isOverview}
            isPartner={isPartner}
            isUserBlocked={isUserBlocked || false}
            onBoardChange={this.handleBoardChanged}
            onBoardCreate={this.handleBoardCreate}
            onBoardGroupChange={this.handleBoardGroupChange}
            profileBoardView={profileBoardView}
            showCreatorProfile={showCreatorProfile}
          />
        ))}
      </Box>
    );
  }

  render() {
    const { boardKeys, isOwnProfile, isOverview, boardsResource } = this.props;
    const { isFetching, isLoaded, isAtEnd, fetchMore } = boardsResource;

    return (
      <TimeSpent
        view={PCONST.thrift.ViewType.USER}
        viewParameter={
          isOwnProfile
            ? PCONST.thrift.ViewParameterType.USER_SELF
            : PCONST.thrift.ViewParameterType.USER_OTHERS
        }
        component={PCONST.thrift.ComponentType.USER_BOARDS}
      >
        <div
          ref={(r) => {
            this.container = r;
          }}
        >
          {isLoaded && this.renderAllBoardsGroups(isOverview ? boardKeys.slice(0, 6) : boardKeys)}
          <Box padding={isFetching ? 6 : 0}>
            <Spinner
              accessibilityLabel={i18n._('Boards', 'List of boards on the users profile page')}
              show={isFetching}
            />
          </Box>
          {isLoaded &&
            !isOverview && (
              <TestableScrollFetch
                isAtEnd={isAtEnd}
                isFetching={isFetching}
                fetchMore={fetchMore}
              />
            )}
        </div>
      </TimeSpent>
    );
  }
}

const boardIsHidden = (deleting, boardId) =>
  deleting[boardId] && (deleting[boardId].success || deleting[boardId].deleting);

export default compose(
  withRouter,
  withResource(
    {
      ...profileBoardsResource,
      key: 'boardsResource',
    },
    { traceLoggerHandlerName: 'pwt/own_profile' },
  ),
  connect(
    createSelector(
      [getProfileBoardFeed, getBoardsDeleting, getProfileBoardView],
      (feed, deleting, profileBoardView) => ({
        boardKeys: (feed || []).reduce(
          (keys, key) =>
            keys.concat(
              key.type === 'board' && key.onProfile && !boardIsHidden(deleting, key.id)
                ? [key]
                : [],
            ),
          [],
        ),
        profileBoardView,
      }),
    ),
    (dispatch: Dispatch<any>): ReduxActionProps => ({
      invalidateFeeds: () => dispatch(invalidateOwnProfileBoards()),
      invalidatePinBetterSaveResource: () =>
        dispatch(
          resourceInvalidate('BoardPickerBoardsResource', { field_set_key: 'board_picker' }),
        ),
    }),
  ),
)(ProfileBoards);
